import { CommonModule, formatDate } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { TranslocoModule } from '@jsverse/transloco';
import { filter, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PendingProviderSelectionService } from 'src/app/core/services';
import { ClientService } from 'src/app/core/services/client.service';
import { Client } from 'src/app/domain/models';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import { Application, ProviderContact } from '../../models';
import { ApplicationService } from '../../services';
import { CurrentApplicationService } from '../../services/current-application.service';

@Component({
    selector: 'app-application-submitted',
    templateUrl: './application-submitted.component.html',
    styleUrls: ['./application-submitted.component.scss'],
    standalone: true,
    imports: [
        TranslocoModule,
        CommonModule,
        MatCard,
        FlexLayoutModule,
        MatButtonModule
    ]
})
export class ApplicationSubmittedComponent implements OnInit {
    providerContacts: ProviderContact[] | undefined;
    client: Client | undefined;
    application: Application | undefined;
    headerHtml: string = '';
    footerHtml: string = '';

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly applicationService: ApplicationService,
        private readonly currentAppService: CurrentApplicationService,
        private readonly _client: ClientService,
        private _googleAnalyticsService: GoogleAnalyticsService,
        private readonly _pendingSelection: PendingProviderSelectionService
    ) {
        this.currentAppService.load();

        // The flow is over, so nothing about it should linger.  In particular the staged pathway
        // is what locks a deep-linked student out of the provider search, and this page invites
        // them to go and browse it — the link below is a plain anchor in client-configured HTML,
        // so it is a full page load and the guard has no loaded application to reason about
        // (LA-22939).
        this._pendingSelection.clearAll();
    }

    ngOnInit() {
        this._googleAnalyticsService.pageView('Application: Submitted');
        this._client.latest
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((client) => {
                this.client = client;
            });
        this.currentAppService.latest
            .pipe(
                filter(
                    (application) =>
                        application !== null &&
                        application !== undefined &&
                        application.applicationId != undefined
                ),
                switchMap((application: Application | undefined) => {
                    this.application = application;
                    this.headerHtml = this.normalizeText(
                        this.client?.submittedPageHeaderHtml || ''
                    );
                    this.footerHtml = this.normalizeText(
                        this.client?.submittedPageFooterHtml || ''
                    );
                    return this.applicationService.getSelectedProviderContacts(
                        application!.applicationId!
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((result) => {
                this.providerContacts = result;
            });
    }

    normalizeText(template: string): string {
        const formattedDate = formatDate(
            this.application?.submitDate || new Date(),
            'MM/dd/yyyy',
            'en-US'
        );
        template = template.replace('{date}', formattedDate);

        template = template.replace(
            '{provider_link}',
            `${runtimeConfig.clientCode}/provider`
        );

        return template;
    }
}
